
import { useEffect, useMemo, useState } from 'react';
import {
  apiErrorText,
  approvalCommitteesApi,
  ticketsApi,
  type CommitteeBrief,
  type UserSearchResult,
} from '@/shared/api';
import {
  approvalTargetProblem,
  availableModes,
  buildApprovalInputs,
  committeeDelivery,
  type ApprovalAccessScope,
  type ApprovalTargetMode,
  type ApprovalTransitionSpec,
} from '@/entities/ticket';
import {
  AsyncUserSelect,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  Field,
  Input,
  Select,
} from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { committeeStructureText } from '@/shared/lib/approval-level';
import { AccessScopeChecks, TransitionFields } from './TransitionFields';
import type { TemplateContext } from '@/features/tickets/ui/TemplatePanel';
import {
  collectTransitionInputs,
  FULL_ACCESS_SCOPE,
  hasUnfilledRequired,
  type TransitionValues,
} from './transition-inputs';
import styles from '../TicketDetail.module.css';

type AutoTarget = { committee: CommitteeBrief | null } | undefined;

export type ApprovalSubmit =
  | { kind: 'transition'; inputs: Record<string, unknown> }
  | {
      kind: 'round';
      committeeId: string;
      subject?: string;
      scope?: ApprovalAccessScope;
    };

const MODE_LABEL: Record<ApprovalTargetMode, string> = {
  auto: 'По правилам',
  committee: 'Комитет',
  person: 'Человек',
};

export const ApprovalTransitionDialog: React.FC<{
  open: boolean;
  ticketId: string;
  spec: ApprovalTransitionSpec;
  title: string;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (submit: ApprovalSubmit) => void;
  templateContext?: TemplateContext | null;
}> = ({ open, ticketId, spec, title, loading, error, onClose, onConfirm, templateContext }) => {
  const modes = useMemo(() => availableModes(spec), [spec]);
  const [mode, setMode] = useState<ApprovalTargetMode>('auto');
  const [committeeId, setCommitteeId] = useState('');
  const [approver, setApprover] = useState<UserSearchResult | null>(null);
  const [subject, setSubject] = useState('');
  const [scope, setScope] = useState(FULL_ACCESS_SCOPE);
  const [restValues, setRestValues] = useState<TransitionValues>({});
  const [restNames, setRestNames] = useState<Record<string, string>>({});

  const [committees, setCommittees] = useState<CommitteeBrief[]>([]);
  const [committeesError, setCommitteesError] = useState<string | null>(null);
  const [committeesLoaded, setCommitteesLoaded] = useState(false);
  const [autoTarget, setAutoTarget] = useState<AutoTarget>(undefined);
  const [autoLoading, setAutoLoading] = useState(true);

  useEffect(() => {
    approvalCommitteesApi
      .listBrief()
      .then((res) => {
        setCommittees(res.data);
        setCommitteesError(null);
      })
      .catch((err) => setCommitteesError(apiErrorText(err, 'Не удалось загрузить комитеты')))
      .finally(() => setCommitteesLoaded(true));
    ticketsApi
      .approvalTarget(ticketId)
      .then((res) => setAutoTarget(res.data))
      .catch(() => setAutoTarget(undefined))
      .finally(() => setAutoLoading(false));
  }, [ticketId]);

  const target =
    mode === 'committee'
      ? ({ mode, committeeId } as const)
      : mode === 'person'
        ? ({ mode, approverId: approver?.id ?? '' } as const)
        : ({ mode } as const);

  const selectedCommittee = committees.find((c) => c.id === committeeId) ?? null;
  const autoIsEmpty = autoTarget !== undefined && autoTarget.committee === null;

  const problem =
    approvalTargetProblem(spec, target) ??
    (mode === 'auto' && autoIsEmpty
      ? 'Подбирать нечего: правил и комитета по умолчанию для этой заявки нет'
      : null) ??
    (spec.subjectRequired && !subject.trim() ? `Заполните «${spec.subjectLabel}»` : null) ??
    (hasUnfilledRequired(spec.rest, restValues) ? 'Заполните обязательные поля' : null);

  const handleConfirm = () => {
    if (problem) return;
    if (target.mode === 'committee' && committeeDelivery(spec) === 'round') {
      onConfirm({
        kind: 'round',
        committeeId: target.committeeId,
        subject: subject.trim() || undefined,
        scope: scope.canViewChat && scope.canViewAttachments ? undefined : scope,
      });
      return;
    }
    onConfirm({
      kind: 'transition',
      inputs: buildApprovalInputs(spec, {
        target,
        subject,
        scope,
        rest: collectTransitionInputs(spec.rest, restValues, scope),
      }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !loading && onClose()}>
      <DialogContent>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>Кто принимает решение по заявке.</DialogDescription>

        <div className={styles.transitionForm}>
          <div className={styles.approvalModes} role="radiogroup" aria-label="Кому отправить">
            {modes.map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                className={cn(styles.approvalMode, mode === m && styles.approvalModeActive)}
                onClick={() => setMode(m)}
                disabled={loading}
              >
                {MODE_LABEL[m]}
              </button>
            ))}
          </div>

          {mode === 'auto' && (
            <p className={styles.actionHint}>
              {autoLoading
                ? 'Смотрим, кто согласует…'
                : autoTarget === undefined
                  ? 'Комитет подберётся по правилам заявки.'
                  : autoTarget.committee
                    ? `Уйдёт в «${autoTarget.committee.name}»: ${committeeStructureText(autoTarget.committee.levels)}.`
                    : 'Правил и комитета по умолчанию для этой заявки нет — выберите комитет или человека.'}
            </p>
          )}

          {mode === 'committee' && (
            <div>
              {committeesError ? (
                <p className={styles.actionHint}>{committeesError}</p>
              ) : !committeesLoaded ? (
                <p className={styles.actionHint}>Загружаем комитеты…</p>
              ) : committees.length === 0 ? (
                <p className={styles.actionHint}>
                  Комитетов нет — их создаёт администратор в разделе «Комитеты согласования».
                </p>
              ) : (
                <>
                  <Field label="Комитет согласования">
                    <Select
                      value={committeeId}
                      onValueChange={setCommitteeId}
                      options={committees.map((c) => ({ value: c.id, label: c.name }))}
                      placeholder="Выберите комитет"
                      disabled={loading}
                    />
                  </Field>
                  { }
                  {selectedCommittee && (
                    <p className={styles.actionHint}>{committeeStructureText(selectedCommittee.levels)}</p>
                  )}
                </>
              )}
            </div>
          )}

          {mode === 'person' && (
            <div>
              <AsyncUserSelect
                placeholder="Согласующий..."
                onSelect={setApprover}
                disabled={loading}
              />
              {approver && (
                <p className={styles.actionHint}>
                  Решение примет {approver.displayName ?? approver.id}.
                </p>
              )}
            </div>
          )}

          {spec.subjectKey && (
            <Field label={spec.subjectLabel} required={spec.subjectRequired}>
              <Input
                id="approval-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Например: закупка лицензий на 12 мест"
              />
            </Field>
          )}

          {spec.scopeKey && (
            <div>
              <span className={cn(styles.subLabel, styles.subLabelFlushTop)}>{spec.scopeLabel}</span>
              <AccessScopeChecks scope={scope} onChange={setScope} />
            </div>
          )}

          <TransitionFields
            specs={spec.rest}
            values={restValues}
            onChange={(key, value) => setRestValues((v) => ({ ...v, [key]: value }))}
            userNames={restNames}
            onPickUser={(key, user) => {
              setRestValues((v) => ({ ...v, [key]: user.id }));
              setRestNames((v) => ({ ...v, [key]: user.displayName ?? user.id }));
            }}
            scope={scope}
            onScopeChange={setScope}
            templateContext={templateContext}
          />

          { }
          {error && <p className={styles.error}>{error}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={loading || problem != null}
          >
            {loading ? 'Отправляем…' : 'Отправить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
